import React from 'react';
import { Document, Page, View, Text, Image } from '@react-pdf/renderer';
import dawnLogo from '../../../assets/WM-Logo-CB-Transparent.png';
import buildStyles from './ReportPdfStyles';

/**
 * Full PDF document — single page definition with auto-pagination.
 * React-PDF handles page breaks automatically via wrap={false} on rows.
 * Pages are always full — no partial pages with whitespace.
 */
const ReportPdfDocument = ({
  config,
  columns,
  columnWidths,
  data,
  parameters,
  logoSrc
}) => {
  const styles = buildStyles(config.layout);
  const zebraStriping = config.layout?.zebraStriping !== false;
  const timestamp = new Date().toLocaleString();

  return (
    <Document
      title={config.reportName}
      author={config.branding?.companyName || 'Wunderlich Malec Engineering'}
      subject={config.description || ''}
    >
      <Page
        size={config.layout?.defaultPageSize?.toUpperCase() === 'LETTER' ? 'LETTER' : 'TABLOID'}
        orientation="landscape"
        style={styles.page}
      >
        {/* ===== Page Header (repeats on every page) ===== */}
        <View style={styles.headerContainer} fixed>
          <View style={styles.headerLeft}>
            {logoSrc && (
              <Image src={logoSrc} style={styles.headerLogo} />
            )}
            <Text style={styles.headerAddress}>
              {config.branding?.companyAddress || '6101 Blue Circle Dr. Eden Prairie, MN 55343'}
            </Text>
            <Text style={styles.headerAddress}>
              {config.branding?.companyPhone || 'Tel: 952-933-3222 | Fax: 952-933-0608'}
            </Text>
          </View>

          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle}>{config.reportName}</Text>
          </View>

          <View style={styles.headerRight}>
            {[0, 2].map(rowStart => {
              const rowParams = (config.parameters || []).slice(rowStart, rowStart + 2);
              const getParamPx = (p) => p.headerPixelWidth || config.columns?.find(c => c.field === p.field)?.pixelWidth || 80;
              const rowTotal = rowParams.reduce((sum, p) => sum + getParamPx(p), 0);
              return (
                <View key={rowStart} style={styles.paramRow}>
                  {rowParams.map(param => {
                    const px = getParamPx(param);
                    const sels = parameters[param.field] || [];
                    return (
                      <View key={param.field} style={[styles.paramColumn, { width: `${((px / rowTotal) * 100).toFixed(1)}%` }]}>
                        <Text style={styles.paramLabel}>{param.label}</Text>
                        {sels.length === 0 ? (
                          <Text style={styles.paramNone}>None Selected</Text>
                        ) : (
                          sels.map((sel, i) => (
                            <Text key={i} style={styles.paramValue}>{sel.fullDisplay || sel.display}</Text>
                          ))
                        )}
                      </View>
                    );
                  })}
                </View>
              );
            })}
          </View>
        </View>

        {/* ===== Column Headers (repeats on every page) ===== */}
        <View style={styles.tableHeaderRow} fixed>
          {columns.map(col => (
            <View key={col.field} style={[styles.tableHeaderCell, {
              width: columnWidths[col.field],
              textAlign: col.align || 'left'
            }]}>
              <Text>{col.label}</Text>
            </View>
          ))}
        </View>

        {/* ===== Data Rows (auto-paginate, wrap={false} keeps rows intact) ===== */}
        {data.map((row, idx) => {
          const isZebra = zebraStriping && idx % 2 === 1;
          return (
            <View key={row.id || idx} style={isZebra ? styles.tableRowZebra : styles.tableRow} wrap={false}>
              {columns.map(col => {
                const camelField = col.field.charAt(0).toLowerCase() + col.field.slice(1);
                const displayKey = `${camelField}_Display`;
                const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];

                return (
                  <View key={col.field} style={[styles.tableCell, {
                    width: columnWidths[col.field],
                    textAlign: col.align || 'left'
                  }]}>
                    <Text>{displayValue != null ? String(displayValue) : ''}</Text>
                  </View>
                );
              })}
            </View>
          );
        })}

        {/* ===== Page Footer (repeats on every page) ===== */}
        <View style={styles.footer} fixed>
          <Text>{timestamp}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Image src={dawnLogo} style={{ width: 14, height: 14 }} />
            <Text>Wunderlich-Malec Confidential</Text>
            <Image src={dawnLogo} style={{ width: 14, height: 14 }} />
          </View>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default ReportPdfDocument;
